import { useEffect, useState } from "react";
import { Package, PackageCheck, Pencil, Plus, RotateCcw, Save, Trash2, Truck, Undo2, Wallet } from "lucide-react";
import { toast } from "sonner";
import { usePaged, ShowMore } from "@/lib/usePaged";
import { api, apiError, fmtDate, money } from "@/lib/api";
import { printSerialSlips } from "@/lib/printSerialSlips";
import { useAuth } from "@/context/AuthContext";
import { can } from "@/lib/rbac";
import { BackdateField } from "@/components/BackdateField";
import { Kpi, PageHead, EmptyState } from "@/components/Bits";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

const STATUSES = ["Submitted", "Confirmed", "Dispatched", "Delivered"];
const LABEL = { Submitted: "Pending", Confirmed: "Confirmed", Dispatched: "Dispatched", Delivered: "Received", Returned: "Returned" };
const statusLabel = (o) =>
  o.status === "Delivered" ? (o.order_type === "b2b" ? "Delivered" : "Received") : LABEL[o.status] || o.status;
const TINT = {
  Submitted: "bg-slate-100 text-slate-700",
  Confirmed: "bg-blue-50 text-blue-700",
  Dispatched: "bg-amber-50 text-amber-700",
  Delivered: "bg-emerald-50 text-emerald-700",
  Returned: "bg-rose-50 text-rose-700",
};
const PAY_TINT = {
  Unpaid: "bg-rose-50 text-rose-700",
  Partial: "bg-amber-50 text-amber-700",
  Paid: "bg-emerald-50 text-emerald-700",
};
const TODAY = () => new Date().toISOString().slice(0, 10);
const CAT_PRODUCT = { PPF: "Veloskin PPF", Sunfilm: "Velotint Sunfilm" };
const PPF_CAP = 245;

export default function Orders() {
  const { user } = useAuth();
  const isHq = !user.studio_id;
  const canCatalog = can(user, "catalog:write");
  const isAdmin = canCatalog || (isHq && can(user, "orders:manage"));
  const canOrder = can(user, "orders:write");
  const canDispatch = ["network_admin", "accountant", "sales_manager_products"].includes(user.role);
  const canPay = ["network_admin", "accountant"].includes(user.role);
  const canReceive = canOrder && (!!user.studio_id || (isHq && can(user, "orders:manage")));
  const [catalog, setCatalog] = useState([]);
  const [orders, setOrders] = useState([]);
  const [studios, setStudios] = useState([]);
  const [status, setStatus] = useState("all");
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [orderStudio, setOrderStudio] = useState("");
  const [orderEntryDate, setOrderEntryDate] = useState("");
  const [lines, setLines] = useState([{ sku_id: "", quantity: "" }]);
  const [editSku, setEditSku] = useState(null);
  const [prices, setPrices] = useState({ hq_cost_price: "", hq_sale_price: "" });
  const [newSku, setNewSku] = useState(null);
  const [del, setDel] = useState(null);
  const [correction, setCorrection] = useState(null);
  const [dispatch, setDispatch] = useState(null);
  const [dispatchBusy, setDispatchBusy] = useState(false);
  const [lrEdit, setLrEdit] = useState(null);
  const [payment, setPayment] = useState(null);
  const [priceEdit, setPriceEdit] = useState(null);
  const [returnOrder, setReturnOrder] = useState(null);
  const [returnReason, setReturnReason] = useState("");
  const [returnDate, setReturnDate] = useState("");
  const canReturn = ["network_admin", "accountant"].includes(user.role);
  const canEditPrice = ["network_admin", "accountant"].includes(user.role);

  const load = () => {
    api.get("/catalog").then(({ data }) => setCatalog(data));
    api
      .get("/material-orders", { params: status !== "all" ? { status } : {} })
      .then(({ data }) => setOrders(data));
  };
  useEffect(load, [status]);
  useEffect(() => {
    if (isHq) api.get("/studios").then(({ data }) => setStudios(data));
  }, [isHq]);

  const openNew = () => {
    setEditId(null);
    setOrderStudio("");
    setLines([{ sku_id: "", quantity: "", price: "" }]);
    setOpen(true);
  };

  const openEditOrder = (o) => {
    setEditId(o.id);
    setOrderStudio(o.studio_id);
    setLines(o.items.map((i) => ({ sku_id: i.sku_id, quantity: String(i.quantity),
                                   serial_number: i.serial_number || "",
                                   price: String(i.hq_sale_price ?? "") })));
    setOpen(true);
  };

  const skuCategory = (id) => (catalog.find((c) => c.id === id) || {}).category || "";
  const skuPrice = (id) => (catalog.find((c) => c.id === id) || {}).hq_sale_price;
  const needsSerial = (id) => /PPF|FILM|SUN/i.test(skuCategory(id));
  const lineAmount = (l) => {
    const qty = Number(l.quantity) || 0;
    const price =
      canEditPrice && l.price !== "" && l.price != null
        ? Number(l.price)
        : Number(skuPrice(l.sku_id) || 0);
    return qty * price;
  };
  const orderSubtotal = lines.reduce((s, l) => (l.sku_id ? s + lineAmount(l) : s), 0);

  const orderRollCats = (order) => {
    const cats = new Set();
    (order?.items || []).forEach((it) => {
      const c = it.category || (catalog.find((x) => x.id === it.sku_id || x.name === it.name) || {}).category;
      if (c === "PPF" || c === "Sunfilm") cats.add(c);
    });
    return [...cats];
  };
  const dispatchCats = dispatch ? orderRollCats(dispatch.order) : [];

  const orderRollSpecs = (order) => {
    const specs = [];
    (order?.items || []).forEach((it) => {
      const c = it.category || (catalog.find((x) => x.id === it.sku_id || x.name === it.name) || {}).category;
      if (c === "PPF") {
        specs.push({ category: "PPF", product_type: CAT_PRODUCT.PPF, label: it.name || CAT_PRODUCT.PPF,
                     quantity: String(it.quantity || 1), capacity_sqft: String(PPF_CAP) });
      } else if (c === "Sunfilm") {
        // Each Velotint product line gets its OWN serial (e.g. Velotint 50% and Velotint 70% are
        // tracked separately), with capacity = the sqft ordered on that line.
        specs.push({ category: "Sunfilm", product_type: CAT_PRODUCT.Sunfilm, label: it.name || "Velotint",
                     quantity: "1", capacity_sqft: String(it.quantity || "") });
      }
    });
    return specs;
  };

  const setGen = (idx, key, val) =>
    setDispatch((d) => ({ ...d, gen: d.gen.map((g, i) => (i === idx ? { ...g, [key]: val } : g)) }));

  const submitOrder = async () => {
    const incomplete = lines.find((l) => l.sku_id && !(Number(l.quantity) > 0));
    if (incomplete) {
      toast.error("Enter a quantity for every product you've added");
      return;
    }
    const payload = {
      items: lines
        .filter((l) => l.sku_id && Number(l.quantity) > 0)
        .map((l) => ({ sku_id: l.sku_id, quantity: Number(l.quantity),
                       serial_number: needsSerial(l.sku_id) ? (l.serial_number || "") : "",
                       ...(canEditPrice && l.price !== "" && l.price != null
                         ? { price: Number(l.price) }
                         : {}) })),
      ...(isHq ? { studio_id: orderStudio } : {}),
      ...(orderEntryDate ? { entry_date: orderEntryDate } : {}),
    };
    try {
      if (editId) {
        await api.put(`/material-orders/${editId}`, payload);
        toast.success("Order updated");
      } else {
        await api.post("/material-orders", payload);
        toast.success(isHq ? "Product sale recorded against the studio" : "Purchase order submitted to HQ");
      }
      setOpen(false);
      setEditId(null);
      setLines([{ sku_id: "", quantity: "", price: "" }]);
      setOrderEntryDate("");
      load();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  };

  const receive = async (order) => {
    try {
      const q = user.backdate_mode && order.created_at
        ? `?entry_date=${order.created_at.slice(0, 10)}`
        : "";
      await api.post(`/material-orders/${order.id}/receive${q}`);
      toast.success(`Received — ${order.studio_name} stock increased`);
      load();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  };

  const confirmOrder = async (order) => {
    try {
      await api.put(`/material-orders/${order.id}/status`, { status: "Confirmed" });
      toast.success("Order confirmed");
      load();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  };

  const deliverB2B = async (order) => {
    try {
      await api.put(`/material-orders/${order.id}/status`, { status: "Delivered" });
      toast.success(`Delivered to ${order.b2b_customer_name || "customer"}`);
      load();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  };

  const submitReturn = async () => {
    if (!returnReason.trim()) return toast.error("Enter a reason for the return");
    try {
      await api.post(`/material-orders/${returnOrder.id}/return`, {
        reason: returnReason.trim(), date: returnDate || null,
      });
      toast.success("Order returned — stock reversed and serials voided");
      setReturnOrder(null);
      setReturnReason("");
      setReturnDate("");
      load();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  };

  const raiseReplacement = async (order) => {
    try {
      await api.post(`/material-orders/${order.id}/replacement`);
      toast.success("Replacement order raised");
      load();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  };


  const openDispatch = (order) => {
    setDispatch({
      order,
      lr_number: "",
      courier_name: "",
      dispatch_date: TODAY(),
      gen: orderRollCats(order).length ? orderRollSpecs(order) : [],
    });
  };

  const saveLr = async () => {
    try {
      await api.post(`/material-orders/${lrEdit.order.id}/lr`, {
        lr_number: lrEdit.lr_number.trim(),
        courier_name: lrEdit.courier_name.trim() || null,
      });
      toast.success("LR number updated");
      setLrEdit(null);
      load();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  };

  const saveDispatch = async () => {
    if (dispatchBusy) return;
    if (!dispatch.courier_name.trim()) {
      toast.error("Enter the courier / transport name (LR can be added later)");
      return;
    }
    const gen = dispatch.gen || [];
    if (dispatchCats.length) {
      for (const g of gen) {
        if (g.category === "PPF" && !(Number(g.quantity) > 0)) {
          toast.error(`Enter the number of ${g.product_type} rolls to dispatch`);
          return;
        }
        if (g.category === "Sunfilm" && !(Number(g.capacity_sqft) > 0)) {
          toast.error("Enter the total sqft for the Velotint Sunfilm dispatch");
          return;
        }
      }
    }
    setDispatchBusy(true);
    try {
      const { data } = await api.post(`/material-orders/${dispatch.order.id}/dispatch`, {
        lr_number: dispatch.lr_number.trim(),
        courier_name: dispatch.courier_name.trim(),
        dispatch_date: dispatch.dispatch_date,
        generate: dispatchCats.length
          ? gen.map((g) => ({
              product_type: g.product_type,
              // Sunfilm dispatches as exactly ONE serial carrying the full ordered sqft.
              quantity: g.category === "Sunfilm" ? 1 : Number(g.quantity),
              capacity_sqft: g.category === "Sunfilm" ? Number(g.capacity_sqft) : PPF_CAP,
            }))
          : [],
      });
      toast.success("Order dispatched — studio notified");
      if (data?.dispatched_serials?.length) printSerialSlips(data.dispatched_serials, {
        studioName: (data.detailer_name || data.b2b_customer_name || data.studio_name || "").replace("B2B · ", ""),
        accreditation: data.order_type === "b2b" ? "Non-accredited" : "Accredited",
      });
      setDispatch(null);
      load();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    } finally {
      setDispatchBusy(false);
    }
  };

  const openPayment = (order) =>
    setPayment({ order, amount: "", date: TODAY(), reference: "" });

  const openPriceEdit = (order) =>
    setPriceEdit({
      order,
      reason: "",
      lines: (order.items || []).map((i) => ({
        sku_id: i.sku_id,
        name: i.name,
        unit: i.unit,
        quantity: i.quantity,
        price: String(i.hq_sale_price ?? ""),
      })),
    });

  const savePriceEdit = async () => {
    try {
      const { data } = await api.put(`/material-orders/${priceEdit.order.id}/price`, {
        lines: priceEdit.lines.map((l) => ({ sku_id: l.sku_id, price: Number(l.price || 0) })),
        reason: priceEdit.reason.trim(),
      });
      setOrders((os) => os.map((o) => (o.id === data.id ? data : o)));
      toast.success("Price updated and logged on the order");
      setPriceEdit(null);
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  };

  const savePayment = async () => {
    if (!(Number(payment.amount) > 0)) {
      toast.error("Enter a payment amount");
      return;
    }
    try {
      await api.post(`/material-orders/${payment.order.id}/payment`, {
        amount: Number(payment.amount),
        date: payment.date,
        reference: payment.reference.trim(),
      });
      toast.success("Payment recorded");
      setPayment(null);
      load();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  };

  const deletePayment = async (orderId, paymentId) => {
    try {
      const { data } = await api.delete(`/material-orders/${orderId}/payment/${paymentId}`);
      setPayment((p) => (p ? { ...p, order: data } : p));
      setOrders((os) => os.map((o) => (o.id === orderId ? data : o)));
      toast.success("Payment removed");
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  };

  const confirmDelete = async () => {
    try {
      await api.delete(`/material-orders/${del.id}`);
      toast.success("Order deleted");
      setDel(null);
      load();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  };

  const openCorrection = (order) => {
    setCorrection({
      order,
      reason: "",
      items: (order.items || []).map((l) => ({ name: l.name, unit: l.unit,
        was: l.quantity, quantity: String(l.quantity) })),
    });
  };

  const saveCorrection = async () => {
    try {
      await api.post(`/material-orders/${correction.order.id}/correct-delivery`, {
        reason: correction.reason,
        items: correction.items.map((l) => ({ name: l.name, quantity: Number(l.quantity || 0) })),
      });
      toast.success("Delivered quantity corrected — studio stock adjusted");
      setCorrection(null);
      load();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  };

  const savePrices = async () => {
    try {
      await api.put(`/catalog/${editSku.id}`, {
        name: editSku.name,
        category: editSku.category,
        unit: editSku.unit,
        hq_cost_price: Number(prices.hq_cost_price || 0),
        hq_sale_price: Number(prices.hq_sale_price || 0),
      });
      toast.success("Catalog prices updated");
      setEditSku(null);
      load();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  };

  const addSku = async () => {
    try {
      await api.post("/catalog", {
        ...newSku,
        hq_cost_price: Number(newSku.hq_cost_price || 0),
        hq_sale_price: Number(newSku.hq_sale_price || 0),
      });
      toast.success("SKU added to catalog");
      setNewSku(null);
      load();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  };

  const orderPaged = usePaged(orders);
  const catalogPaged = usePaged(catalog);
  const pipeline = STATUSES.map((s) => ({ s, n: orders.filter((o) => o.status === s).length }));

  return (
    <div>
      <PageHead
        title="Material Orders"
        subtitle={isAdmin ? "Incoming purchase orders from all studios (HQ B2B)" : `${user.studio_name} · orders to HQ`}
      >
        {canOrder && (
          <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) setEditId(null); }}>
            <Button data-testid="new-order-btn" onClick={openNew}>
              <Plus className="mr-2 h-4 w-4" />
              {isHq ? "Record product sale" : "New purchase order"}
            </Button>
            <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-lg">
              <DialogHeader>
                <DialogTitle>
                  {editId
                    ? "Edit order"
                    : isHq
                      ? "Record a product sale to a studio"
                      : "Order materials from HQ"}
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-3">
                {isHq && (
                  <div>
                    <Label>Studio *</Label>
                    <Select value={orderStudio} onValueChange={setOrderStudio} disabled={!!editId}>
                      <SelectTrigger className="mt-1.5 min-h-[44px]" data-testid="order-studio-select">
                        <SelectValue placeholder="Which studio is this sale for?" />
                      </SelectTrigger>
                      <SelectContent>
                        {studios.map((s) => (
                          <SelectItem key={s.id} value={s.id}>
                            {s.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <p className="mt-1.5 text-xs text-slate-500">
                      Priced from the HQ catalog. It becomes HQ B2B revenue, and the studio's stock
                      goes up when the order is marked received.
                    </p>
                  </div>
                )}
                {lines.map((l, idx) => (
                  <div key={idx} className="flex flex-wrap gap-3">
                    <Select
                      value={l.sku_id}
                      onValueChange={(v) =>
                        setLines((ls) =>
                          ls.map((x, i) =>
                            i === idx
                              ? {
                                  ...x,
                                  sku_id: v,
                                  price: canEditPrice ? String(skuPrice(v) ?? "") : x.price,
                                }
                              : x
                          )
                        )
                      }
                    >
                      <SelectTrigger className="min-w-[240px] flex-1" data-testid={`order-sku-select-${idx}`}>
                        <SelectValue placeholder="Select SKU" />
                      </SelectTrigger>
                      <SelectContent>
                        {catalog.map((c) => (
                          <SelectItem key={c.id} value={c.id}>
                            {c.name} — {money(c.hq_sale_price)}/{c.unit}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Input
                      className="w-24"
                      placeholder="Qty"
                      data-testid={`order-qty-input-${idx}`}
                      value={l.quantity}
                      onChange={(e) =>
                        setLines((ls) =>
                          ls.map((x, i) => (i === idx ? { ...x, quantity: e.target.value } : x))
                        )
                      }
                    />
                    {canEditPrice && (
                      <Input
                        className="w-32"
                        type="number"
                        min="0"
                        step="0.01"
                        placeholder="Price"
                        title="Sale price per unit (editable)"
                        data-testid={`order-price-input-${idx}`}
                        value={l.price ?? ""}
                        onChange={(e) =>
                          setLines((ls) =>
                            ls.map((x, i) => (i === idx ? { ...x, price: e.target.value } : x))
                          )
                        }
                      />
                    )}
                    {canEditPrice && l.sku_id && (
                      <span
                        className="flex items-center text-sm font-medium text-slate-600"
                        data-testid={`order-line-amount-${idx}`}
                      >
                        = {money(lineAmount(l))}
                      </span>
                    )}
                    {lines.length > 1 && (
                      <Button
                        size="sm"
                        variant="ghost"
                        className="text-rose-600"
                        data-testid={`remove-order-line-${idx}`}
                        onClick={() => setLines((ls) => ls.filter((_, i) => i !== idx))}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    )}
                    {needsSerial(l.sku_id) && (
                      <Input
                        className="w-full"
                        placeholder="PPF / sun-film serial number (for warranty)"
                        data-testid={`order-serial-input-${idx}`}
                        value={l.serial_number || ""}
                        onChange={(e) =>
                          setLines((ls) =>
                            ls.map((x, i) => (i === idx ? { ...x, serial_number: e.target.value } : x))
                          )
                        }
                      />
                    )}
                  </div>
                ))}
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <Button
                    size="sm"
                    variant="outline"
                    data-testid="add-order-line-btn"
                    onClick={() => setLines((ls) => [...ls, { sku_id: "", quantity: "", price: "" }])}
                  >
                    <Plus className="mr-2 h-3.5 w-3.5" /> Add another product
                  </Button>
                  <div
                    className="text-right text-sm font-semibold text-brand-ink"
                    data-testid="order-running-total"
                  >
                    <span className="text-xs font-normal text-slate-500">Order total (excl. GST): </span>
                    {money(orderSubtotal)}
                  </div>
                </div>
              </div>
              <BackdateField value={orderEntryDate} onChange={setOrderEntryDate} />
              <DialogFooter>
                <Button
                  data-testid="submit-order-btn"
                  onClick={submitOrder}
                  disabled={
                    (!editId && isHq && !orderStudio) ||
                    !lines.some((l) => l.sku_id && Number(l.quantity) > 0)
                  }
                >
                  {editId ? "Save changes" : isHq ? "Record sale" : "Submit order"}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}
        {canCatalog && (
          <Button
            variant="outline"
            data-testid="add-sku-btn"
            onClick={() => setNewSku({ name: "", category: "PPF Film", unit: "sqft", hq_cost_price: "", hq_sale_price: "" })}
          >
            <Plus className="mr-2 h-4 w-4" /> Catalog SKU
          </Button>
        )}
      </PageHead>

      <div className="mb-6 grid gap-6 sm:grid-cols-4">
        {pipeline.map(({ s, n }) => (
          <Kpi key={s} label={LABEL[s]} value={n} testid={`kpi-orders-${s}`} />
        ))}
      </div>

      <div className="mb-4">
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="w-[200px]" data-testid="order-status-filter">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            {STATUSES.map((s) => (
              <SelectItem key={s} value={s}>
                {LABEL[s]}
              </SelectItem>
            ))}
            <SelectItem value="Returned">Returned</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <Card className="border-brand-line shadow-none">
        <CardHeader>
          <CardTitle className="text-lg">Orders</CardTitle>
        </CardHeader>
        <CardContent className="overflow-x-auto p-0">
          <Table data-testid="orders-table" className="mobile-cards">
            <TableHeader>
              <TableRow>
                {isHq && <TableHead>Detailer / Studio</TableHead>}
                <TableHead>Placed</TableHead>
                <TableHead>SKUs</TableHead>
                <TableHead className="text-right">Order value</TableHead>
                {isAdmin && <TableHead className="text-right">HQ profit</TableHead>}
                <TableHead>Status</TableHead>
                {canOrder && <TableHead />}
              </TableRow>
            </TableHeader>
            <TableBody>
              {orders.length === 0 && (
                <TableRow>
                  <TableCell colSpan={9} className="p-0">
                    <EmptyState
                      icon={Package}
                      testid="orders-empty-state"
                      title="No material orders yet"
                      hint={
                        canOrder
                          ? "Raise your first purchase order to HQ — delivered orders automatically top up studio stock."
                          : "Studio purchase orders to HQ will show up here."
                      }
                      action={
                        canOrder ? (
                          <Button data-testid="orders-empty-add-btn" onClick={openNew}>
                            <Plus className="mr-2 h-4 w-4" />
                            {isHq ? "Record product sale" : "New purchase order"}
                          </Button>
                        ) : null
                      }
                    />
                  </TableCell>
                </TableRow>
              )}
              {orderPaged.visible.map((o) => (
                <TableRow key={o.id} className="hover:bg-brand-tint/60">
                  {isHq && (
                    <TableCell className="font-medium" data-testid={`order-party-${o.id}`}>
                      <span className="text-sm font-semibold text-brand-ink">
                        {o.order_type === "b2b"
                          ? (o.b2b_customer_name || "Detailer")
                          : (o.studio_name || "—")}
                      </span>
                      <span className="mt-0.5 block text-[11px] font-normal text-slate-400">
                        {o.order_type === "b2b" ? "Non-accredited detailer (B2B)" : "Accredited detailer"}
                      </span>
                      {o.order_type === "b2b" && o.b2b_customer_contact && (
                        <span className="block text-[11px] font-normal text-slate-400">{o.b2b_customer_contact}</span>
                      )}
                    </TableCell>
                  )}
                  <TableCell className="text-slate-500">{fmtDate(o.created_at)}</TableCell>
                  <TableCell className="max-w-sm">
                    {o.items.map((i) => (
                      <p key={i.sku_id + i.name} className="text-xs text-slate-600">
                        {i.name} — {i.quantity} {i.unit}
                        {i.serial_number ? ` · SN ${i.serial_number}` : ""}
                      </p>
                    ))}
                  </TableCell>
                  <TableCell className="text-right">{money(o.revenue)}</TableCell>
                  {isAdmin && (
                    <TableCell className="text-right font-semibold text-emerald-600">
                      {money(o.profit)}
                    </TableCell>
                  )}
                  <TableCell>
                    <div className="flex flex-wrap items-center gap-1.5">
                      <Badge data-testid={`order-status-${o.id}`} className={`border-0 ${TINT[o.status]}`}>
                        <Package className="mr-1 h-3 w-3" /> {statusLabel(o)}
                      </Badge>
                      <Badge
                        data-testid={`order-payment-${o.id}`}
                        className={`border-0 ${PAY_TINT[o.payment_status || "Unpaid"]}`}
                      >
                        <Wallet className="mr-1 h-3 w-3" /> {o.payment_status || "Unpaid"}
                      </Badge>
                      {o.raised_by === "HQ" && (
                        <Badge
                          data-testid={`order-source-${o.id}`}
                          className="border-0 bg-violet-50 text-violet-700"
                        >
                          {o.order_type === "b2b" ? "B2B" : "HQ sale"}
                        </Badge>
                      )}
                    </div>
                    {(o.amount_paid > 0 || (o.total_payable || o.revenue) > 0) && (
                      <p className="mt-1 text-[11px] text-slate-500" data-testid={`order-paid-${o.id}`}>
                        Paid {money(o.amount_paid || 0)} of {money(o.total_payable || o.revenue || 0)}
                      </p>
                    )}
                    {(o.status === "Dispatched" || o.status === "Delivered") && (
                      <p className="mt-1 text-[11px] text-amber-700" data-testid={`order-lr-${o.id}`}>
                        {o.lr_number ? `LR ${o.lr_number}` : "LR pending"}
                        {o.courier_name ? ` · ${o.courier_name}` : ""}
                        {o.dispatch_date ? ` · ${fmtDate(o.dispatch_date)}` : ""}
                      </p>
                    )}
                    {o.received && (
                      <p className="mt-1 text-[11px] text-emerald-700" data-testid={`order-received-${o.id}`}>
                        Received {fmtDate(o.received_at)}
                        {o.received_by ? ` · ${o.received_by}` : ""}
                      </p>
                    )}
                    {o.status === "Returned" && (
                      <p className="mt-1 text-[11px] text-rose-700" data-testid={`order-returned-${o.id}`}>
                        Returned {fmtDate(o.returned_at)}
                        {o.returned_by ? ` · ${o.returned_by}` : ""}
                        {o.return_reason ? ` · ${o.return_reason}` : ""}
                      </p>
                    )}
                  </TableCell>
                  {canOrder && (
                    <TableCell className="text-right">
                      <div className="flex flex-wrap justify-end gap-2">
                        {/* Accountant / Admin only: set or edit price before confirmation. SMP can view but not edit. */}
                        {["network_admin", "accountant"].includes(user.role) &&
                          o.status === "Submitted" && !o.received && (
                            <Button
                              size="sm"
                              variant="outline"
                              data-testid={`edit-price-${o.id}`}
                              onClick={() => openPriceEdit(o)}
                            >
                              Edit price
                            </Button>
                          )}
                        {/* HQ: confirm */}
                        {canDispatch && o.status === "Submitted" && (
                          <Button
                            size="sm"
                            variant="outline"
                            data-testid={`confirm-order-${o.id}`}
                            onClick={() => confirmOrder(o)}
                          >
                            Confirm
                          </Button>
                        )}
                        {/* HQ: dispatch with LR */}
                        {canDispatch && (o.status === "Confirmed" || o.status === "Submitted") && (
                          <Button
                            size="sm"
                            data-testid={`dispatch-order-${o.id}`}
                            onClick={() => openDispatch(o)}
                          >
                            <Truck className="mr-1.5 h-3.5 w-3.5" /> Dispatch
                          </Button>
                        )}
                        {/* Studio: receive (studio orders only, once dispatched) */}
                        {canReceive && o.order_type !== "b2b" && o.status === "Dispatched" && !o.received && (
                          <Button
                            size="sm"
                            data-testid={`receive-order-${o.id}`}
                            onClick={() => receive(o)}
                          >
                            <PackageCheck className="mr-1.5 h-3.5 w-3.5" /> Mark received
                          </Button>
                        )}
                        {/* HQ view of studio order awaiting receipt */}
                        {canDispatch && !canReceive && o.order_type !== "b2b" && o.status === "Dispatched" && !o.received && (
                          <span className="text-[11px] text-slate-400" data-testid={`await-receipt-${o.id}`}>
                            Awaiting studio receipt
                          </span>
                        )}
                        {/* HQ: add / update LR after dispatch */}
                        {canDispatch && (o.status === "Dispatched" || o.status === "Delivered") && (
                          <Button
                            size="sm"
                            variant="outline"
                            data-testid={`edit-lr-${o.id}`}
                            onClick={() => setLrEdit({ order: o, lr_number: o.lr_number || "", courier_name: o.courier_name || "" })}
                          >
                            {o.lr_number ? "Edit LR" : "Add LR"}
                          </Button>
                        )}
                        {/* HQ: reprint the roll serial slips shipped with this order */}
                        {canDispatch && o.dispatched_serials?.length > 0 && (
                          <Button
                            size="sm"
                            variant="outline"
                            data-testid={`reprint-slips-${o.id}`}
                            onClick={() => printSerialSlips(o.dispatched_serials, {
                              studioName: (o.detailer_name || o.b2b_customer_name || o.studio_name || "").replace("B2B · ", ""),
                              accreditation: o.order_type === "b2b" ? "Non-accredited" : "Accredited",
                            })}
                          >
                            Reprint slips
                          </Button>
                        )}
                        {/* HQ: mark B2B delivered */}
                        {canDispatch && o.order_type === "b2b" && o.status === "Dispatched" && (
                          <Button
                            size="sm"
                            data-testid={`deliver-b2b-${o.id}`}
                            onClick={() => deliverB2B(o)}
                          >
                            <PackageCheck className="mr-1.5 h-3.5 w-3.5" /> Mark delivered
                          </Button>
                        )}
                        {o.order_type === "b2b" && o.status === "Delivered" && (
                          <Badge
                            data-testid={`b2b-closed-${o.id}`}
                            className="border-0 bg-emerald-50 text-emerald-700"
                          >
                            Delivered to customer
                          </Badge>
                        )}
                        {/* Accountant: record payment */}
                        {canPay && (
                          <Button
                            size="sm"
                            variant="outline"
                            data-testid={`record-payment-${o.id}`}
                            onClick={() => openPayment(o)}
                          >
                            <Wallet className="mr-1.5 h-3.5 w-3.5" /> Payment
                          </Button>
                        )}
                        {/* HQ: correct delivered studio quantity */}
                        {isAdmin && o.order_type !== "b2b" && o.received && (
                          <Button
                            size="sm"
                            variant="outline"
                            data-testid={`correct-delivery-${o.id}`}
                            onClick={() => openCorrection(o)}
                          >
                            <Pencil className="mr-1.5 h-3.5 w-3.5" /> Correct quantity
                          </Button>
                        )}
                        {/* Admin/Accountant: return a confirmed/dispatched/received order */}
                        {canReturn && !["Submitted", "Returned"].includes(o.status)
                          && (o.status === "Confirmed" || o.status === "Dispatched" || o.status === "Delivered") && (
                          <Button
                            size="sm"
                            variant="outline"
                            className="border-rose-200 text-rose-600 hover:bg-rose-50 hover:text-rose-700"
                            data-testid={`return-order-${o.id}`}
                            onClick={() => { setReturnOrder(o); setReturnReason(""); setReturnDate(""); }}
                          >
                            <Undo2 className="mr-1.5 h-3.5 w-3.5" /> Return
                          </Button>
                        )}
                        {/* Admin/Accountant: raise a corrected replacement for a returned order */}
                        {canReturn && o.status === "Returned" && !o.replaced_by_order_id && (
                          <Button
                            size="sm"
                            data-testid={`raise-replacement-${o.id}`}
                            onClick={() => raiseReplacement(o)}
                          >
                            <RotateCcw className="mr-1.5 h-3.5 w-3.5" /> Raise replacement
                          </Button>
                        )}
                        {o.status === "Returned" && o.replaced_by_order_id && (
                          <Badge className="border-0 bg-slate-100 text-slate-600" data-testid={`replacement-raised-${o.id}`}>
                            Replacement raised
                          </Badge>
                        )}
                        {/* Edit / delete only while pending */}
                        {o.status === "Submitted" && (
                          <Button
                            size="sm"
                            variant="outline"
                            data-testid={`edit-order-${o.id}`}
                            onClick={() => openEditOrder(o)}
                          >
                            <Pencil className="mr-1.5 h-3.5 w-3.5" /> Edit
                          </Button>
                        )}
                        {o.status === "Submitted" && (
                          <Button
                            size="sm"
                            variant="outline"
                            className="border-rose-200 text-rose-600 hover:bg-rose-50 hover:text-rose-700"
                            data-testid={`delete-order-${o.id}`}
                            onClick={() => setDel(o)}
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <ShowMore paged={orderPaged} testid="orders-show-more" />
        </CardContent>
      </Card>

      <Card className="mt-6 border-brand-line shadow-none">
        <CardHeader>
          <CardTitle className="text-lg">Global SKU catalog</CardTitle>
        </CardHeader>
        <CardContent className="overflow-x-auto p-0">
          <Table data-testid="catalog-table" className="mobile-cards">
            <TableHeader>
              <TableRow>
                <TableHead>SKU</TableHead>
                <TableHead>Category</TableHead>
                <TableHead>Unit</TableHead>
                {isAdmin && <TableHead className="text-right">HQ cost price</TableHead>}
                <TableHead className="text-right">HQ sale price</TableHead>
                {isAdmin && <TableHead className="text-right">Margin</TableHead>}
                {canCatalog && <TableHead />}
              </TableRow>
            </TableHeader>
            <TableBody>
              {catalog.map((c) => (
                <TableRow key={c.id} className="hover:bg-brand-tint/60">
                  <TableCell className="font-medium">{c.name}</TableCell>
                  <TableCell className="text-slate-500">{c.category}</TableCell>
                  <TableCell className="text-slate-500">{c.unit}</TableCell>
                  {isAdmin && <TableCell className="text-right">{money(c.hq_cost_price)}</TableCell>}
                  <TableCell className="text-right">{money(c.hq_sale_price)}</TableCell>
                  {isAdmin && (
                    <TableCell className="text-right font-semibold text-emerald-600">
                      {money(c.hq_sale_price - c.hq_cost_price)}
                    </TableCell>
                  )}
                  {canCatalog && (
                    <TableCell className="text-right">
                      <Button
                        size="sm"
                        variant="ghost"
                        data-testid={`edit-sku-${c.id}`}
                        onClick={() => {
                          setEditSku(c);
                          setPrices({
                            hq_cost_price: String(c.hq_cost_price),
                            hq_sale_price: String(c.hq_sale_price),
                          });
                        }}
                      >
                        Edit prices
                      </Button>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <ShowMore paged={catalogPaged} testid="catalog-show-more" />
        </CardContent>
      </Card>

      <Dialog open={!!editSku} onOpenChange={(o) => !o && setEditSku(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{editSku?.name} — HQ prices</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>HQ cost price (₹ per {editSku?.unit})</Label>
              <Input
                className="mt-1.5"
                data-testid="sku-cost-price-input"
                value={prices.hq_cost_price}
                onChange={(e) => setPrices((p) => ({ ...p, hq_cost_price: e.target.value }))}
              />
            </div>
            <div>
              <Label>HQ sale price (₹ per {editSku?.unit})</Label>
              <Input
                className="mt-1.5"
                data-testid="sku-sale-price-input"
                value={prices.hq_sale_price}
                onChange={(e) => setPrices((p) => ({ ...p, hq_sale_price: e.target.value }))}
              />
            </div>
          </div>
          <DialogFooter>
            <Button data-testid="save-sku-prices-btn" onClick={savePrices}>
              <Save className="mr-2 h-4 w-4" /> Save prices
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!newSku} onOpenChange={(o) => !o && setNewSku(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>New catalog SKU</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {[
              ["name", "Name"],
              ["category", "Category"],
              ["unit", "Unit"],
              ["hq_cost_price", "HQ cost price (₹)"],
              ["hq_sale_price", "HQ sale price (₹)"],
            ].map(([k, label]) => (
              <div key={k}>
                <Label>{label}</Label>
                <Input
                  className="mt-1.5"
                  data-testid={`new-sku-${k}-input`}
                  value={newSku?.[k] ?? ""}
                  onChange={(e) => setNewSku((n) => ({ ...n, [k]: e.target.value }))}
                />
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button data-testid="save-new-sku-btn" onClick={addSku} disabled={!newSku?.name}>
              Add SKU
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog open={!!correction} onOpenChange={(o) => !o && setCorrection(null)}>
        <DialogContent className="max-h-[80vh] overflow-y-auto sm:max-w-lg" data-testid="correct-delivery-dialog">
          <DialogHeader>
            <DialogTitle>Correct delivered quantity</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-slate-500">
            {correction?.order?.studio_name} · order {correction?.order?.id?.slice(-6)}. The
            difference is applied to the studio's stock, the HQ warehouse and the delivery ledger,
            so nobody has to hand-edit a quantity.
          </p>
          <div className="space-y-3">
            {(correction?.items || []).map((l, idx) => (
              <div key={l.name} className="flex flex-wrap items-end justify-between gap-3">
                <div>
                  <p className="text-sm font-medium text-brand-ink">{l.name}</p>
                  <p className="text-xs text-slate-500">
                    Delivered {l.was} {l.unit}
                  </p>
                </div>
                <Input
                  className="min-h-[44px] w-32 text-right"
                  inputMode="decimal"
                  data-testid={`correct-qty-input-${idx}`}
                  value={l.quantity}
                  onChange={(e) =>
                    setCorrection((c) => ({
                      ...c,
                      items: c.items.map((x, i) =>
                        i === idx ? { ...x, quantity: e.target.value } : x
                      ),
                    }))
                  }
                />
              </div>
            ))}
            <div>
              <Label className="text-xs">Reason</Label>
              <Input
                className="mt-1 min-h-[44px]"
                placeholder="Short delivery, damaged roll, miscount…"
                data-testid="correct-delivery-reason-input"
                value={correction?.reason || ""}
                onChange={(e) => setCorrection((c) => ({ ...c, reason: e.target.value }))}
              />
            </div>
            {(correction?.order?.corrections || []).length > 0 && (
              <div className="rounded-md border border-brand-line p-3 text-xs text-slate-500">
                <p className="font-semibold text-slate-700">Earlier corrections</p>
                {correction.order.corrections.map((c, i) => (
                  <p key={i}>
                    {fmtDate(c.at)} · {c.by}
                    {c.reason ? ` · ${c.reason}` : ""} ·{" "}
                    {c.changes.map((ch) => `${ch.name} ${ch.delta > 0 ? "+" : ""}${ch.delta}`).join(", ")}
                  </p>
                ))}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" className="min-h-[44px]" onClick={() => setCorrection(null)}>
              Cancel
            </Button>
            <Button className="min-h-[44px]" data-testid="save-correction-btn" onClick={saveCorrection}>
              Apply correction
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!returnOrder} onOpenChange={(o) => !o && setReturnOrder(null)}>
        <DialogContent className="sm:max-w-md" data-testid="return-order-dialog">
          <DialogHeader>
            <DialogTitle>Return this order?</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-slate-600">
            {returnOrder?.studio_name} · {returnOrder?.items?.length || 0} SKU(s). This reverses any
            stock it added to the studio, voids all of its roll serial numbers so they can never be
            used on a job or warranty, and keeps the order visible with a <b>Returned</b> status. Any
            roll already used blocks the return.
          </p>
          <div className="space-y-3">
            <div>
              <Label>Reason for return *</Label>
              <Input
                data-testid="return-reason-input"
                value={returnReason}
                onChange={(e) => setReturnReason(e.target.value)}
                placeholder="e.g. Wrong product, damaged in transit"
              />
            </div>
            <BackdateField value={returnDate} onChange={setReturnDate} />
          </div>
          <DialogFooter>
            <Button variant="outline" className="min-h-[44px]" onClick={() => setReturnOrder(null)}>
              Cancel
            </Button>
            <Button
              className="min-h-[44px] bg-rose-600 text-white hover:bg-rose-700"
              data-testid="confirm-return-order-btn"
              onClick={submitReturn}
            >
              Return order
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>


      <Dialog open={!!del} onOpenChange={(o) => !o && setDel(null)}>
        <DialogContent className="sm:max-w-md" data-testid="delete-order-dialog">
          <DialogHeader>
            <DialogTitle>Delete this order?</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-slate-600">
            {del?.studio_name} · {money(del?.revenue || 0)} · {del?.items?.length || 0} SKU(s). Only
            orders that have not been received can be deleted, so nothing already counted in HQ
            revenue or studio stock is affected.
          </p>
          <DialogFooter>
            <Button variant="outline" className="min-h-[44px]" onClick={() => setDel(null)}>
              Cancel
            </Button>
            <Button
              className="min-h-[44px] bg-rose-600 text-white hover:bg-rose-700"
              data-testid="confirm-delete-order-btn"
              onClick={confirmDelete}
            >
              Delete order
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!dispatch} onOpenChange={(o) => !o && setDispatch(null)}>
        <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-md" data-testid="dispatch-order-dialog">
          <DialogHeader>
            <DialogTitle>Dispatch order</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-slate-600">
              {dispatch?.order?.studio_name} · {money(dispatch?.order?.revenue || 0)}
            </p>
            <div>
              <Label className="text-xs">LR number (lorry receipt) — optional</Label>
              <Input
                className="mt-1 min-h-[44px]"
                data-testid="dispatch-lr-input"
                placeholder="Leave blank to add later"
                value={dispatch?.lr_number || ""}
                onChange={(e) => setDispatch((d) => ({ ...d, lr_number: e.target.value }))}
              />
            </div>
            <div>
              <Label className="text-xs">Courier / transport name *</Label>
              <Input
                className="mt-1 min-h-[44px]"
                data-testid="dispatch-courier-input"
                value={dispatch?.courier_name || ""}
                onChange={(e) => setDispatch((d) => ({ ...d, courier_name: e.target.value }))}
              />
            </div>
            <div>
              <Label className="text-xs">Dispatch date</Label>
              <Input
                type="date"
                className="mt-1 min-h-[44px]"
                data-testid="dispatch-date-input"
                value={dispatch?.dispatch_date || ""}
                onChange={(e) => setDispatch((d) => ({ ...d, dispatch_date: e.target.value }))}
              />
            </div>
            {dispatchCats.length > 0 && (
              <div className="space-y-2 rounded-md border border-brand-line p-3" data-testid="dispatch-serial-gen">
                <Label className="text-xs">Generate roll serial numbers ({dispatchCats.join(" / ")})</Label>
                {(dispatch.gen || []).map((g, idx) => (
                  <div
                    key={`${g.category}-${idx}`}
                    className="rounded border border-brand-line/60 p-2"
                    data-testid={`gen-row-${g.category}-${idx}`}
                  >
                    <p className="text-sm font-medium">{g.label || g.product_type}</p>
                    {g.category === "Sunfilm" ? (
                      <div className="mt-2">
                        <Label className="text-[11px] text-slate-500">
                          Sqft dispatched (its own serial number)
                        </Label>
                        <Input
                          type="number"
                          min="1"
                          className="mt-1 min-h-[40px]"
                          data-testid={`gen-cap-Sunfilm-${idx}`}
                          placeholder="e.g. 200"
                          value={g.capacity_sqft}
                          onChange={(e) => setGen(idx, "capacity_sqft", e.target.value)}
                        />
                        <p className="mt-1 text-[11px] text-slate-400">
                          A separate serial number is generated for {g.label || "this Velotint film"}, carrying {Number(g.capacity_sqft) || 0} sqft.
                        </p>
                      </div>
                    ) : (
                    <div className="mt-2 grid grid-cols-2 gap-2">
                      <div>
                        <Label className="text-[11px] text-slate-500">Rolls to dispatch</Label>
                        <Input
                          type="number"
                          min="1"
                          className="mt-1 min-h-[40px]"
                          data-testid={`gen-qty-${g.category}-${idx}`}
                          value={g.quantity}
                          onChange={(e) => setGen(idx, "quantity", e.target.value)}
                        />
                      </div>
                      <div>
                        <Label className="text-[11px] text-slate-500">Capacity (sqft/roll)</Label>
                        <Input
                          className="mt-1 min-h-[40px] bg-slate-50"
                          data-testid={`gen-cap-PPF-${idx}`}
                          value="245"
                          disabled
                        />
                      </div>
                    </div>
                    )}
                  </div>
                ))}
                <p className="text-[11px] text-slate-500">
                  Serials are generated at dispatch and slips print automatically.
                </p>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" className="min-h-[44px]" onClick={() => setDispatch(null)}>
              Cancel
            </Button>
            <Button className="min-h-[44px]" data-testid="save-dispatch-btn" disabled={dispatchBusy} onClick={saveDispatch}>
              <Truck className="mr-1.5 h-4 w-4" /> {dispatchBusy ? "Dispatching…" : "Mark dispatched"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!lrEdit} onOpenChange={(o) => !o && setLrEdit(null)}>
        <DialogContent className="sm:max-w-md" data-testid="lr-edit-dialog">
          <DialogHeader>
            <DialogTitle>{lrEdit?.order?.lr_number ? "Update" : "Add"} LR number</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-slate-600">
              {lrEdit?.order?.studio_name} · dispatched
              {lrEdit?.order?.dispatch_date ? ` ${fmtDate(lrEdit.order.dispatch_date)}` : ""}
            </p>
            <div>
              <Label className="text-xs">LR number (lorry receipt)</Label>
              <Input
                className="mt-1 min-h-[44px]"
                data-testid="lr-edit-input"
                value={lrEdit?.lr_number || ""}
                onChange={(e) => setLrEdit((d) => ({ ...d, lr_number: e.target.value }))}
              />
            </div>
            <div>
              <Label className="text-xs">Courier / transport name</Label>
              <Input
                className="mt-1 min-h-[44px]"
                data-testid="lr-edit-courier"
                value={lrEdit?.courier_name || ""}
                onChange={(e) => setLrEdit((d) => ({ ...d, courier_name: e.target.value }))}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" className="min-h-[44px]" onClick={() => setLrEdit(null)}>
              Cancel
            </Button>
            <Button className="min-h-[44px]" data-testid="save-lr-btn" onClick={saveLr}>
              Save LR
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!priceEdit} onOpenChange={(o) => !o && setPriceEdit(null)}>
        <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-md" data-testid="price-edit-dialog">
          <DialogHeader>
            <DialogTitle>Edit sale price</DialogTitle>
          </DialogHeader>
          {priceEdit && (
            <div className="space-y-4">
              <p className="text-xs text-slate-500">
                Update the unit sale price for {priceEdit.order.studio_name || "this order"}. The
                change is logged against the order. Prices lock once the order is confirmed.
              </p>
              {priceEdit.lines.map((l, idx) => (
                <div key={l.sku_id} className="rounded-md border border-brand-line p-3">
                  <p className="text-sm font-medium text-brand-ink">{l.name}</p>
                  <p className="mb-2 text-xs text-slate-500">
                    {l.quantity} {l.unit}
                  </p>
                  <Label>Unit price (₹)</Label>
                  <Input
                    type="number"
                    min="0"
                    inputMode="decimal"
                    data-testid={`price-edit-input-${l.sku_id}`}
                    className="mt-1.5 min-h-[44px]"
                    value={l.price}
                    onChange={(e) =>
                      setPriceEdit((p) => ({
                        ...p,
                        lines: p.lines.map((x, i) =>
                          i === idx ? { ...x, price: e.target.value } : x,
                        ),
                      }))
                    }
                  />
                  <p className="mt-1 text-[11px] text-slate-500">
                    Line total: ₹{(Number(l.price || 0) * Number(l.quantity || 0)).toLocaleString("en-IN")}
                  </p>
                </div>
              ))}
              <div>
                <Label>Reason (optional)</Label>
                <Input
                  data-testid="price-edit-reason"
                  className="mt-1.5 min-h-[44px]"
                  value={priceEdit.reason}
                  onChange={(e) => setPriceEdit((p) => ({ ...p, reason: e.target.value }))}
                  placeholder="e.g. Negotiated rate"
                />
              </div>
              {priceEdit.order.price_changes?.length > 0 && (
                <div className="rounded-md bg-slate-50 p-3 text-xs text-slate-600" data-testid="price-change-log">
                  <p className="mb-1 font-medium text-slate-700">Change history</p>
                  {priceEdit.order.price_changes.map((c, i) => (
                    <p key={i}>
                      {fmtDate(c.at)} · {c.by} ·{" "}
                      {c.changes.map((ch) => `${ch.name} ₹${ch.old_price}→₹${ch.new_price}`).join(", ")}
                      {c.reason ? ` (${c.reason})` : ""}
                    </p>
                  ))}
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button data-testid="save-price-edit-btn" onClick={savePriceEdit}>
              Save price
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>


      <Dialog open={!!payment} onOpenChange={(o) => !o && setPayment(null)}>
        <DialogContent className="sm:max-w-md" data-testid="payment-order-dialog">
          <DialogHeader>
            <DialogTitle>Record payment</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="rounded-md border border-brand-line p-3 text-sm">
              <p className="font-medium text-brand-ink">{payment?.order?.studio_name}</p>
              <p className="text-slate-500">
                Order value {money(payment?.order?.total_payable || payment?.order?.revenue || 0)} ·
                Paid {money(payment?.order?.amount_paid || 0)} ·{" "}
                <span className="font-semibold">
                  Outstanding{" "}
                  {money(
                    Math.max(
                      0,
                      (payment?.order?.total_payable || payment?.order?.revenue || 0) -
                        (payment?.order?.amount_paid || 0)
                    )
                  )}
                </span>
              </p>
            </div>
            <div className="flex gap-3">
              <div className="flex-1">
                <Label className="text-xs">Amount received *</Label>
                <Input
                  className="mt-1 min-h-[44px]"
                  inputMode="decimal"
                  data-testid="payment-amount-input"
                  value={payment?.amount || ""}
                  onChange={(e) => setPayment((p) => ({ ...p, amount: e.target.value }))}
                />
              </div>
              <div className="flex-1">
                <Label className="text-xs">Payment date</Label>
                <Input
                  type="date"
                  className="mt-1 min-h-[44px]"
                  data-testid="payment-date-input"
                  value={payment?.date || ""}
                  onChange={(e) => setPayment((p) => ({ ...p, date: e.target.value }))}
                />
              </div>
            </div>
            <div>
              <Label className="text-xs">Reference number</Label>
              <Input
                className="mt-1 min-h-[44px]"
                placeholder="UTR / cheque / txn ref (optional)"
                data-testid="payment-reference-input"
                value={payment?.reference || ""}
                onChange={(e) => setPayment((p) => ({ ...p, reference: e.target.value }))}
              />
            </div>
            {(payment?.order?.payments || []).length > 0 && (
              <div className="rounded-md border border-brand-line p-3 text-xs">
                <p className="mb-1 font-semibold text-slate-700">Payment history</p>
                {payment.order.payments.map((p) => (
                  <div key={p.id} className="flex items-center justify-between py-0.5">
                    <span className="text-slate-600">
                      {fmtDate(p.date)} · {money(p.amount)}
                      {p.reference ? ` · ${p.reference}` : ""}
                    </span>
                    <button
                      className="text-rose-600 hover:underline"
                      data-testid={`delete-payment-${p.id}`}
                      onClick={() => deletePayment(payment.order.id, p.id)}
                    >
                      remove
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" className="min-h-[44px]" onClick={() => setPayment(null)}>
              Close
            </Button>
            <Button className="min-h-[44px]" data-testid="save-payment-btn" onClick={savePayment}>
              <Wallet className="mr-1.5 h-4 w-4" /> Record payment
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
